import { useEffect, useMemo, useRef, useState } from "react";
import { CommodityModel, LocationModel, CounterpartyModel, GetCustomerDataResponse, GetCustomerSettingsResponse, YearDataModel, BudgetPricingStrategyModel, BudgetPricingComponentModel, MarketPriceProvider, BudgetRoundModel } from "../../../../../Generated/Commodity-Management-Api";
import { Currency, MarketViewProductModel, Unit } from "../../../../../Generated/Mark-It-View-Api";
import { StoneXRow } from "../../../../StoneX";
import StoneXMainPage from "../../../../StoneX/StoneXMainPage/StoneXMainPage";
import { StoneXAutocomplete, StoneXButton, StoneXModal, StoneXSelect } from "../../../../StoneXMui";
import { useApplicationSelector, RootState } from "../../../../../Redux/ReduxStore";
import {
  ContractDateHelper,
  findMatchingPricingStrategies,
  getMappingsForCommodity,
  hasPricingComponents,
  pricingStrategyCovers,
  resolveContractDate,
} from "../../../../../Helpers";
import {
  CommodityContractMonthMappingModel,
  listCommodityContractMonthMappings,
} from "../../../../../Apis/ContractMonthMappingApi";
import { BudgetRow } from "../UpdateBudgetsPage/BudgetTypes";
import { Alert, Button } from "@mui/material";
import EditBudgetsTable, { EditBudgetsTableRef } from "../EditBudgetsTable";
import { useSnackbar } from "notistack";
import { CommodityManagementApi, MarkItViewApi } from "../../../../../Apis/Apis";
import { BudgetFilters } from "../Types";
import UploadBudgets from "./UploadBudgets";
import { BudgetRoundSelector } from "../../BudgetRoundSelector";
import PricingStrategyEditor, {
  createDefaultBudgetPricingComponent,
  createEmptyBudgetPricingStrategy,
  savePricingStrategy,
} from "../../Settings/PricingStrategyEditor";
import PricingStrategyNotice, { PricingStrategyState } from "./PricingStrategyNotice";

interface AddBudgetsProps {
  years: YearDataModel[];
  pricingStrategies?: BudgetPricingStrategyModel[];
  onSave: (budgetFilter: BudgetFilters) => void;
  onClose: () => void;
  onCancel?: () => void;
}

export default function AddBudgets(props: AddBudgetsProps) {

  const { years, pricingStrategies, onClose, onSave, onCancel } = props;

  const { enqueueSnackbar } = useSnackbar();
  const editBudgetTableRef = useRef<EditBudgetsTableRef>(null);

  const [budgets, setBudgets] = useState<BudgetRow[]>([]);

  const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData);
  const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);
  const [commodityYearOptions, setCommodityYearOptions] = useState<number[]>([]);

  // Held locally rather than read straight from props so a strategy created here shows
  // up immediately, without a page reload or a round trip through AddBudgetsPage.
  const [strategies, setStrategies] = useState<BudgetPricingStrategyModel[]>(pricingStrategies ?? []);
  const [selectedStrategyId, setSelectedStrategyId] = useState<number | null>(null);
  const [skipPricingStrategy, setSkipPricingStrategy] = useState(false);
  const [pricingStrategyLocked, setPricingStrategyLocked] = useState(false); // Disable users from editing fields that might affect the pricing strategy

  // Budget month -> contract month per commodity, from Settings > Commodities.
  const [contractMonthMappings, setContractMonthMappings] = useState<CommodityContractMonthMappingModel[]>([]);

  const [strategyModalOpen, setStrategyModalOpen] = useState(false);
  const [strategyDraft, setStrategyDraft] = useState<BudgetPricingStrategyModel | null>(null);
  const [isSavingStrategy, setIsSavingStrategy] = useState(false);
  const [marketViewProducts, setMarketViewProducts] = useState<MarketViewProductModel[]>([]);

  const [selectedCommodity, setSelectedCommodity] = useState<CommodityModel | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationModel | null>(null);
  const [selectedCounterparty, setSelectedCounterparty] = useState<CounterpartyModel | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>();
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [selectedBudgetRound, setSelectedBudgetRound] = useState<BudgetRoundModel | null>(null);

  const [isSaving, setIsSaving] = useState(false);

  // What the budgets about to be entered are scoped to. A null location or counterparty
  // is meaningful: it only matches a strategy that is not scoped to one either.
  const entryScope = useMemo(() => ({
    commodityId: selectedCommodity?.commodityId,
    locationId: selectedLocation?.locationId ?? null,
    counterpartyId: selectedCounterparty?.counterpartyId ?? null,
  }), [selectedCommodity, selectedLocation, selectedCounterparty]);

  const matchingStrategies = useMemo(
    () => (selectedCommodity ? findMatchingPricingStrategies(strategies, entryScope) : []),
    [strategies, entryScope, selectedCommodity],
  );

  // The user's pick wins; a single match needs no picking. Resolving it here rather
  // than in state means a changed selection can never leave a stale strategy behind.
  const identifiedPricingStrategy = useMemo(() => {
    const chosen = matchingStrategies.find(x => x.budgetPricingStrategyId === selectedStrategyId);
    if (chosen) return chosen;
    return matchingStrategies.length === 1 ? matchingStrategies[0] : null;
  }, [matchingStrategies, selectedStrategyId]);

  const pricingStrategyState = useMemo<PricingStrategyState>(() => {
    if (!selectedCommodity) return { kind: "pending" };
    if (identifiedPricingStrategy) {
      return hasPricingComponents(identifiedPricingStrategy)
        ? { kind: "ready", strategy: identifiedPricingStrategy }
        : { kind: "noComponents", strategy: identifiedPricingStrategy };
    }
    if (matchingStrategies.length > 1) return { kind: "choose", matches: matchingStrategies };
    return { kind: "missing" };
  }, [selectedCommodity, identifiedPricingStrategy, matchingStrategies]);

  const entryDescription = useMemo(() => {
    const parts = [selectedCommodity?.commodityName ?? "this commodity"];
    if (customerSettings.budgetsByLocation) {
      parts.push(selectedLocation?.locationName ?? "no location");
    }
    if (customerSettings.budgetsByCounterparty) {
      parts.push(selectedCounterparty?.counterpartyName ?? "no counterparty");
    }
    return parts.join(" · ");
  }, [selectedCommodity, selectedLocation, selectedCounterparty, customerSettings]);

  // A usable strategy, or an explicit decision to do without one. Commodity, unit and
  // year are checked too - editNewBudgets dereferences all three, so Start used to be
  // able to throw when a single site-wide strategy matched an empty selection.
  const canStart = useMemo(() => {
    if (!selectedCommodity || !selectedUnit || !selectedYear) return false;
    if (customerSettings.usesBudgetRounds && !selectedBudgetRound) return false;
    if (skipPricingStrategy) return true;
    return pricingStrategyState.kind === "ready";
  }, [selectedCommodity, selectedUnit, selectedYear, customerSettings, selectedBudgetRound, skipPricingStrategy, pricingStrategyState]);

  async function saveBudgets() {
    const isValid = await editBudgetTableRef.current!.isValid();
    if (!isValid) {
      enqueueSnackbar('Please fill in all required fields.', { variant: 'error' });
      return;
    }

    const budgets = editBudgetTableRef.current!.getBudgets();

    setIsSaving(true);
    CommodityManagementApi.updateBudgets(undefined, budgets)
      .then(async (res) => {

        const budgetFilter = {
          fiscalYear: selectedYear ?? undefined,
          commodityId: selectedCommodity?.commodityId,
          locationId: selectedLocation?.locationId,
          counterpartyId: selectedCounterparty?.counterpartyId,
          budgetRoundId: selectedBudgetRound?.budgetRoundId,
        };

        await onSave(budgetFilter);
        setIsSaving(false);
        enqueueSnackbar('Budgets were successfully updated', {variant: 'success'});
      })
      .catch(err => {
        enqueueSnackbar('An error occurred while saving the budgets', {variant: 'error'});
        setIsSaving(false);
      });
  }

  function editNewBudgets() {
    const startMonth = ContractDateHelper.getStartContractDate(selectedYear!, customerSettings?.fiscalMonthOffset);
    // No strategy at all is a deliberate path (see skipPricingStrategy): the rows carry
    // a volume and no price components, and the grid renders no price columns.
    const strategy = skipPricingStrategy ? null : identifiedPricingStrategy;
    const hasMarkItView = strategy?.components.some((q) => q.marketPriceProvider === MarketPriceProvider.MarketView) ?? false;

    // The commodity's budget month -> contract month mapping, if it has one. Commodities
    // without one fall back to "contract month = budget month" inside resolveContractDate.
    const mappingsByBudgetMonth = getMappingsForCommodity(contractMonthMappings, selectedCommodity?.commodityId);

    setBudgets(
      [...Array(12)].map((q, i) => {
        const budgetDate = ContractDateHelper.addMonths(startMonth, i);

        return {
          budgetId: 0,
          commodityId: selectedCommodity!.commodityId,
          locationId: selectedLocation?.locationId,
          counterpartyId: selectedCounterparty?.counterpartyId,
          budgetRoundId: selectedBudgetRound?.budgetRoundId,
          pricingStrategyId: strategy?.budgetPricingStrategyId ?? 0,
          volume: 0,
          unit: selectedUnit!,
          currency: Currency.Usd,
          budgetDate,
          contractDate: resolveContractDate(budgetDate.month, budgetDate.year, mappingsByBudgetMonth),
          hasContractDate: hasMarkItView,
          budgetPrices: (strategy?.components ?? []).map((component: BudgetPricingComponentModel) => ({
            componentType: component.componentType,
            unit: selectedUnit!,
            price: 0,
          })),
        } as BudgetRow;
      }),
    );
  }

  function clearBudgets() {
    setBudgets([]);
  }

  function lockInPricingStrategy() {
    setPricingStrategyLocked(true);
    editNewBudgets();
  }

  function unlockPricingStrategy() {
    setPricingStrategyLocked(false);
    clearBudgets();
  }

  function openCreateStrategyModal() {
    // Scoped to what is being entered, EXCEPT the location: a strategy that covers every
    // location is the common case, and narrowing it to one is a decision the user should
    // make deliberately rather than inherit from whatever row they happened to be adding.
    const suggestedName = [selectedCommodity?.commodityName, selectedCounterparty?.counterpartyName]
      .filter(Boolean)
      .join(" - ");

    setStrategyDraft(createEmptyBudgetPricingStrategy({
      name: suggestedName,
      commodityId: selectedCommodity?.commodityId ?? null,
      locationId: null,
      counterpartyId: selectedCounterparty?.counterpartyId ?? null,
      components: [createDefaultBudgetPricingComponent()],
    }));
    setStrategyModalOpen(true);
  }

  function openEditStrategyModal(strategy: BudgetPricingStrategyModel) {
    setStrategyDraft({
      ...strategy,
      components: (strategy.components ?? []).map((component) => ({ ...component })),
      positionStrategies: (strategy.positionStrategies ?? []).map((positionStrategy) => ({
        ...positionStrategy,
        components: positionStrategy.components.map((component) => ({ ...component })),
      })),
    });
    setStrategyModalOpen(true);
  }

  function closeStrategyModal() {
    setStrategyModalOpen(false);
    setStrategyDraft(null);
  }

  async function handleSaveStrategy(strategy: BudgetPricingStrategyModel) {
    setIsSavingStrategy(true);
    try {
      const saved = await savePricingStrategy(strategy);

      setStrategies((previous) => {
        const existingIndex = previous.findIndex(x => x.budgetPricingStrategyId === saved.budgetPricingStrategyId);
        return existingIndex >= 0
          ? previous.map((item, index) => (index === existingIndex ? saved : item))
          : [...previous, saved];
      });

      setSelectedStrategyId(saved.budgetPricingStrategyId);
      setSkipPricingStrategy(false);
      closeStrategyModal();

      // Saving a strategy scoped somewhere else leaves the entry exactly as stuck as it
      // was, so say so rather than letting the same warning reappear unexplained.
      if (pricingStrategyCovers(saved, entryScope)) {
        enqueueSnackbar(`Pricing strategy "${saved.name}" saved.`, { variant: 'success' });
      } else {
        enqueueSnackbar(
          `Saved "${saved.name}", but its commodity, location or counterparty does not cover ${entryDescription}.`,
          { variant: 'warning' },
        );
      }
    } catch {
      enqueueSnackbar('Failed to save the pricing strategy.', { variant: 'error' });
    } finally {
      setIsSavingStrategy(false);
    }
  }

  function setCommodityUnits() {

    if (!selectedCommodity) {
      setSelectedUnit(null);
      return;
    }

    if (!selectedCommodity.displayUnit) {
      return;
    }

    setSelectedUnit(selectedCommodity.displayUnit);
  }

  function updateCommodityYearOptions() {

    if (!selectedCommodity) {
      setCommodityYearOptions([]);
      return;
    }

    const endYear = years
      .filter(y => y.commodityIds.includes(selectedCommodity.commodityId) )
      .sort((a, b) => b.year - a.year)[0]?.year ?? new Date().getFullYear()-1;

      const options = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 1 + i );
    setCommodityYearOptions(options);
  }

  function updateSelectedYear() {
    if (!selectedYear) {
      setSelectedYear(commodityYearOptions?.[0]);
    }
  }

  function reset() {
    setSelectedCommodity(null);
    setSelectedLocation(null);
    setSelectedCounterparty(null);
    setSelectedYear(null);
    setSelectedUnit(null);
    setSelectedBudgetRound(null);
    setSelectedStrategyId(null);
    setSkipPricingStrategy(false);
    unlockPricingStrategy();
  }

  useEffect(setCommodityUnits, [selectedCommodity]);
  useEffect(updateSelectedYear, [commodityYearOptions]);
  useEffect(updateCommodityYearOptions, [selectedCommodity]);

  useEffect(() => setStrategies(pricingStrategies ?? []), [pricingStrategies]);

  useEffect(() => {
    listCommodityContractMonthMappings(null)
      .then((response) => setContractMonthMappings(response.data.rows ?? []))
      .catch(() => {
        // Not fatal - contract months just fall back to the budget month, which is what
        // an unmapped commodity does anyway. The grid columns stay editable.
        setContractMonthMappings([]);
      });
  }, []);

  // A different commodity/location/counterparty is a different question, so any decision
  // to go without a strategy is asked again rather than carried over silently.
  useEffect(() => setSkipPricingStrategy(false), [entryScope]);

  // Only needed by the strategy editor, so it is fetched the first time one is opened.
  useEffect(() => {
    if (!strategyModalOpen || marketViewProducts.length > 0) return;
    MarkItViewApi.listMarketViewProducts(undefined)
      .then(response => setMarketViewProducts(response.data.rows ?? []))
      .catch(() => enqueueSnackbar('Could not load MarketView products.', { variant: 'warning' }));
  }, [strategyModalOpen]);

  return (
    <StoneXMainPage>
      <StoneXRow>
        <StoneXAutocomplete
          options={customerData.commodities ?? []}
          getOptionLabel={(q) => q.commodityName}
          value={selectedCommodity}
          onChange={(e: any, commodity: CommodityModel | null) => setSelectedCommodity(commodity)}
          disabled={isSaving || pricingStrategyLocked}
          isOptionEqualToValue={(a, b) => a.commodityId == b.commodityId}
          label="Commodity"
        />

        {customerSettings.budgetsByLocation && (
          <StoneXAutocomplete
            options={customerData.budgetingLocations ?? []}
            getOptionLabel={(q) => q.locationName}
            value={selectedLocation}
            onChange={(e: any, location: LocationModel | null) => setSelectedLocation(location)}
            disabled={isSaving || pricingStrategyLocked}
            label="Location"
          />
        )}

        {customerSettings.budgetsByCounterparty && (
          <StoneXAutocomplete
            options={customerData.counterparties ?? []}
            getOptionLabel={(q) => q.counterpartyName!}
            value={selectedCounterparty}
            onChange={(e: any, counterparty: CounterpartyModel | null) => setSelectedCounterparty(counterparty)}
            disabled={isSaving || pricingStrategyLocked}
            label="Counterparty"
          />
        )}

        <StoneXAutocomplete
          value={selectedUnit}
          options={customerData.units ?? []}
          getOptionLabel={(unit) => unit}
          onChange={(e: any, unit: Unit | null) => setSelectedUnit(unit)}
          disabled={isSaving || !selectedCommodity || pricingStrategyLocked}
          label="Unit"
        />

        <StoneXSelect
          value={selectedYear}
          options={commodityYearOptions}
          getId={x => x}
          getOptionLabel={x => x}
          disabled={isSaving || !selectedCommodity || pricingStrategyLocked}
          onChange={(e: any) => setSelectedYear(e)}
          label="Year"
        />

        {customerSettings.usesBudgetRounds && (
          <BudgetRoundSelector
            options={customerData.budgetRounds ?? []}
            onChange={setSelectedBudgetRound}
          />
        )}

        {pricingStrategyLocked
          ? <></>
          : <Button disabled={!canStart} onClick={lockInPricingStrategy}>Start</Button>
        }

      </StoneXRow>

      <PricingStrategyNotice
        state={pricingStrategyState}
        customerData={customerData}
        entryDescription={entryDescription}
        isSkipping={skipPricingStrategy}
        disabled={isSaving || pricingStrategyLocked}
        onSelectStrategy={(strategy) => setSelectedStrategyId(strategy?.budgetPricingStrategyId ?? null)}
        onCreateStrategy={openCreateStrategyModal}
        onEditStrategy={openEditStrategyModal}
        onSkip={() => setSkipPricingStrategy(true)}
        onCancelSkip={() => setSkipPricingStrategy(false)}
      />

      <div style={{display: 'flex', flexDirection: 'column', flexGrow: 1}}>
        {pricingStrategyLocked &&
          <EditBudgetsTable ref={editBudgetTableRef} budgets={budgets} isSaving={isSaving} displayPrecision={selectedCommodity?.displayPrecision} />
        }
      </div>

      <StoneXRow>
        <div className="pull-right nested-row-item">
          <StoneXButton variant='outlined' onClick={onCancel} disabled={isSaving}>Cancel</StoneXButton>
          <StoneXButton variant='outlined' onClick={reset} disabled={isSaving}>Reset</StoneXButton>
          <StoneXButton onClick={saveBudgets} disabled={isSaving || !pricingStrategyLocked} isWorking={isSaving}>Add</StoneXButton>
        </div>
      </StoneXRow>

      <UploadBudgets pricingStrategies={strategies} />

      <StoneXModal
        header={strategyDraft?.budgetPricingStrategyId ? "Edit Pricing Strategy" : "Add Pricing Strategy"}
        open={strategyModalOpen && strategyDraft !== null}
        onClose={closeStrategyModal}
      >
        {strategyDraft !== null && (
          <PricingStrategyEditor
            strategy={strategyDraft}
            customerData={customerData}
            customerSettings={customerSettings}
            marketViewProducts={marketViewProducts}
            usesSeperatePricingStrategies={customerSettings?.separatePricingStrategiesForPositions ?? false}
            intro={
              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                This strategy will be used for <strong>{entryDescription}</strong> budgets.
                {customerSettings.budgetsByLocation && ' Location is left blank so it applies to every location - set it only if this strategy is location specific.'}
                {' '}It is saved to Settings and available everywhere, not just here.
              </Alert>
            }
            onCancel={closeStrategyModal}
            onSave={handleSaveStrategy}
            onChange={setStrategyDraft}
            isWorking={isSavingStrategy}
          />
        )}
      </StoneXModal>

    </StoneXMainPage>
  );
}
